<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import LoginButton from '@/components/buttons/login-button.vue'
import LogoutButton from './buttons/logout-button.vue'
import { useAuth } from '@/auth/auth'
import MessageUsageBar from '@/components/MessageUsageBar.vue'
import { useCurrentUser } from '@/composables/useCurrentUser.js'
import { MessageUsageKind } from '@/models/messageUsage.js'

const { user, isAuthenticated } = useAuth()
const { currentUser } = useCurrentUser()
const textMessageUsage = computed(
  () => currentUser.value?.messageUsage?.[MessageUsageKind.TEXT] ?? null,
)
const audioTtsMessageUsage = computed(
  () => currentUser.value?.messageUsage?.[MessageUsageKind.AUDIO_TTS] ?? null,
)
const topBarElement = ref(null)
let topBarResizeObserver = null

const updateTopBarHeight = () => {
  if (!topBarElement.value) return

  document.documentElement.style.setProperty(
    '--top-bar-height',
    `${topBarElement.value.offsetHeight}px`,
  )
}

onMounted(() => {
  updateTopBarHeight()

  if (!window.ResizeObserver || !topBarElement.value) return

  topBarResizeObserver = new ResizeObserver(updateTopBarHeight)
  topBarResizeObserver.observe(topBarElement.value)
})

onBeforeUnmount(() => {
  topBarResizeObserver?.disconnect()
  document.documentElement.style.removeProperty('--top-bar-height')
})
</script>

<template>
  <div ref="topBarElement" class="top-bar">
    <div class="brand-group">
      <RouterLink class="logo" to="/">GPTeacher</RouterLink>
      <nav class="nav-links" aria-label="Primary navigation">
        <RouterLink v-if="isAuthenticated" class="nav-link" to="/chat">Chat</RouterLink>
        <RouterLink class="nav-link" to="/number-trainer">Number Trainer</RouterLink>
        <RouterLink v-if="isAuthenticated" class="nav-link" to="/vocabulary-banks"
          >Vocabulary Banks</RouterLink
        >
        <RouterLink v-if="isAuthenticated" class="nav-link" to="/account">Account</RouterLink>
      </nav>
    </div>
    <div
      class="user-section"
      :class="{
        'user-section--authenticated': isAuthenticated,
        'user-section--guest': !isAuthenticated,
      }"
    >
      <template v-if="isAuthenticated">
        <div class="usage-bars" aria-label="Usage limits">
          <MessageUsageBar
            :value="textMessageUsage?.usedMessages"
            :limit="textMessageUsage?.currentMessageLimit"
            description-label="text messages"
            compact-description-label="text"
            color="gray"
          />
          <MessageUsageBar
            :value="audioTtsMessageUsage?.usedMessages"
            :limit="audioTtsMessageUsage?.currentMessageLimit"
            description-label="audio messages"
            compact-description-label="audio"
            color="violet"
          />
        </div>
        <div class="user-actions">
          <span class="user-name">{{ user?.name }}</span>
          <LogoutButton />
        </div>
      </template>
      <template v-if="!isAuthenticated">
        <LoginButton />
      </template>
    </div>
  </div>
</template>

<style scoped>
.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: #202020;
  box-shadow: 0 2px 4px rgba(255, 255, 255, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.brand-group {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.logo {
  font-size: 1.5rem;
  font-weight: bold;
  color: #4caf50;
  text-decoration: none;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.nav-link {
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  color: #d8dfd6;
  text-decoration: none;
}

.nav-link.router-link-active {
  background-color: rgba(76, 175, 80, 0.18);
  color: #7fd48d;
}

.user-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.usage-bars {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.user-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-name {
  font-weight: 500;
  color: #e2e2e2;
}

.login-button {
  padding: 0.5rem 1rem;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.login-button:hover {
  background-color: #45a049;
}

@media (max-width: 860px) {
  .top-bar {
    flex-wrap: wrap;
    padding: 0.85rem 1rem;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .brand-group {
    order: 1;
    flex-direction: column;
    align-items: flex-start;
    flex: 1 1 auto;
    gap: 0.5rem;
  }

  .nav-links {
    flex-wrap: wrap;
  }

  .user-section--authenticated {
    display: contents;
  }

  .user-section--guest {
    order: 2;
    margin-left: auto;
  }

  .user-actions {
    order: 2;
    justify-content: flex-end;
    margin-left: auto;
  }

  .usage-bars {
    order: 3;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    flex: 1 0 100%;
    width: 100%;
    gap: 0.55rem;
  }
}
</style>
